import {Button, Tag, Toggle} from '@/components/atoms'
import {Modal} from '@/components/atoms'
import {usePromotion} from '@/context/Promotion/PromotionContext'
import {useSegmentsOperations} from '@/hooks/promotion/segments/useSegmentsOperations'
import {setToastNotifications} from '@/store/slices/commonSlice'
import {useEffect, useRef, useState} from 'react'
import {useDispatch} from 'react-redux'
import {MODAL_TYPE} from '@/utils/constants/globalStyle'
import {ROUTES} from '@/utils/constants/routesConstants'
import {SegmentModal} from '../SegmentModal/SegmentModal'
import {SegmentGroup} from './SegmentGroup'

const defaultModalState = {
  show: false,
  type: '',
  groupIndex: null,
  isExcluded: false,
}

const defaultCreateSegmentModalState = {
  show: false,
}

export const SegmentSection = ({isToggleBtnDisable = false}) => {
  const {
    handleAllCustomersToggle,
    handleExcludeToggle,
    isAllCustomers,
    isExcludeEnabled,
    segmentConfiguration,
    handleAddGroup,
    handleSubmitSegments,
    handleDeleteGroup,
    handleRemove,
  } = useSegmentsOperations()

  const dispatch = useDispatch()
  const {handleDraft, promotion} = usePromotion()
  const [segmentModalState, setSegmentModalState] = useState(defaultModalState)
  const [createSegmentModalState, setCreateSegmentModalState] = useState(defaultCreateSegmentModalState)
  const promotionNameRef = useRef('')

  useEffect(() => {
    promotionNameRef.current = promotion?.name?.trim() ?? ''
  }, [promotion?.name])

  const handleSegmentSelection = (items, groupIndex = null) => {
    const type = items?.[0]?.id

    if (!type) return

    const currentPromotionName = promotionNameRef.current

    if (type === 'Create New Segment') {
      if (!currentPromotionName) {
        dispatch(
          setToastNotifications({
            show: true,
            message: 'Promotion Name is required before creating a segment.',
            type: 'error',
          })
        )
        return
      }

      setCreateSegmentModalState({show: true})
      return
    }

    setSegmentModalState({
      show: true,
      type,
      groupIndex,
    })
  }

  const handleCloseSegmentModal = () => {
    setSegmentModalState(defaultModalState)
  }

  const handleCloseCreateSegmentModal = () => {
    setCreateSegmentModalState(defaultCreateSegmentModalState)
  }

  const handleContinueCreateSegment = async () => {
    handleCloseCreateSegmentModal()

    try {
      await handleDraft()
     globalThis.location?.assign?.(ROUTES.PROFILE_SEGMENTS_CREATE)
    } catch (error) {
      return
    }
  }

  const includedSegmentGroups = segmentConfiguration?.includedSegments || [] 

  return (
    <div className="products-admin-segment-section-wrapper flex flex-col gap-6 rounded-lg bg-gray-850 p-6">
      <h3 className="text-lg font-semibold">Segments</h3>
      <Toggle
        updateValue={handleAllCustomersToggle}
        value={isAllCustomers}
        uiSchema={{size: 'small', label: 'All Customers'}}
        disabled={isToggleBtnDisable}
      />

      {!isAllCustomers && (
        <>
          {includedSegmentGroups.map((segmentGroup, index) => (
            <div key={`group-${index}`}>
              <SegmentGroup
                groupIndex={index}
                segmentGroup={segmentGroup}
                onSegmentSelection={items => handleSegmentSelection(items, index)}
                handleDeleteGroup={handleDeleteGroup}
                handleRemove={handleRemove}
              />
              <div className="my-4 border-b border-gray-600"></div>
            </div>
          ))}

          {includedSegmentGroups.length > 1 && (
            <div className="mb-4 flex flex-wrap items-center gap-3">
              {includedSegmentGroups.map((_, index) => (
                <div
                  key={`group-label-${index}`}
                  className="products-admin-segment-section-group-label flex items-center gap-3"
                >
                  <Tag text={`Group ${index + 1}`} color="blue" />
                  {index < includedSegmentGroups.length - 1 && (
                    <span className="text-sm font-medium text-white">OR</span>
                  )}
                </div>
              ))}
            </div>
          )}

          <Button
            handleButton={handleAddGroup}
            uiSchema={{variant: 'tertiary', title: 'Add Group', iconLeading: 'add_box', size: 'medium'}}
          />

          <div className="text-xs">
            <p className="mb-2 text-gray-50">Within one group, users must be in all segments to see the promotion.</p>
            <p className="text-gray-50">
              When there are multiple groups, users can be in either group to see the promotion.
            </p>
          </div>
        </>
      )}

      <div className="h-px bg-gray-600" />
      <Toggle
        isDark
        updateValue={handleExcludeToggle}
        value={isExcludeEnabled}
        uiSchema={{size: 'small', label: 'Exclude'}}
        disabled={isToggleBtnDisable}
      />
      {isExcludeEnabled && (
        <>
          <SegmentGroup
            groupIndex={null}
            segmentGroup={segmentConfiguration?.excludedSegment}
            onSegmentSelection={items => handleSegmentSelection(items, null)}
            handleDeleteGroup={handleDeleteGroup}
            handleRemove={handleRemove}
          />
        </>
      )}

      <SegmentModal
        key={`segment-modal-${segmentModalState.show}-${segmentModalState.type}-${segmentModalState.groupIndex}`}
        show={segmentModalState.show}
        type={segmentModalState.type}
        groupIndex={segmentModalState.groupIndex}
        existingSelections={
          segmentModalState.groupIndex === null
            ? segmentConfiguration?.excludedSegment?.segments
            : segmentConfiguration?.includedSegments?.[segmentModalState.groupIndex]?.segments
        }
        onSubmit={handleSubmitSegments}
        onClose={handleCloseSegmentModal}
      />

      <Modal
        showModal={createSegmentModalState.show}
        title={'Are you sure you want to create a new segment?'}
        text={'Create a new segment if it does not already exist. Proceeding will redirect you to the products page and save this promotion as draft.'}
        primaryButtonDisabled={false}
        primaryButtonText="Continue"
        tertiaryButtonText="Cancel"
        showTertiaryButton={true}
        type={MODAL_TYPE.SMALL}
        onClickTertiaryButton={handleCloseCreateSegmentModal}
        onClickPrimaryButton={handleContinueCreateSegment}
        hideCloseButton={true}
        className="products-promotions-segment-modal"
      />
    </div>
  )
}
